/**
 * Pending Milestone Approvals Component
 * CBO interface for approving/rejecting milestone-based incentive payouts
 * Integrates with payment infrastructure and prepaid card system
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';
import {
  DollarSign,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  CreditCard,
  AlertCircle,
  Eye,
  Filter,
  Download,
  Target
} from 'lucide-react';
import { useTabbedChrome } from '@/components/weavein';

interface PendingMilestoneApprovalsProps {
  programId?: number;
}

interface PendingIncentive {
  id: number;
  payout_number: string;
  member_id: number;
  member_name: string;
  program_id: number;
  program_name: string;
  milestone_id: number;
  milestone_name: string;
  milestone_type: string;
  payout_amount: number;
  cbo_payout_amount: number;
  member_payout_amount: number;
  payout_method: string;
  status: string;
  scheduled_date: string;
  completion_date?: string;
  created_at: string;
}

const PAYOUT_METHODS = [
  { value: 'CARD', label: 'Prepaid Card', icon: CreditCard },
  { value: 'DIRECT_DEPOSIT', label: 'Direct Deposit', icon: DollarSign },
  { value: 'CHECK', label: 'Check', icon: DollarSign },
  { value: 'CASH', label: 'Cash', icon: DollarSign }
];

const PendingMilestoneApprovals: React.FC<PendingMilestoneApprovalsProps> = ({ programId }) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [pendingIncentives, setPendingIncentives] = useState<PendingIncentive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterProgram, setFilterProgram] = useState<string>(programId?.toString() || '');
  const [filterPayoutMethod, setFilterPayoutMethod] = useState<string>('');

  // Modal states
  const [selectedIncentive, setSelectedIncentive] = useState<PendingIncentive | null>(null);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);

  useEffect(() => {
    fetchPendingIncentives();
  }, [programId, filterProgram, filterPayoutMethod]);

  const fetchPendingIncentives = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterProgram) params.append('program_id', filterProgram);
      if (filterPayoutMethod) params.append('payout_method', filterPayoutMethod);
      params.append('status', 'PENDING'); // Only fetch pending approvals

      // Use the new endpoint that fetches milestone-based pending incentives
      const url = programId
        ? getWeaveinApiUrl(`/api/programs/${programId}/incentives/pending?${params.toString()}`)
        : getWeaveinApiUrl(`/api/member-incentive-payouts?${params.toString()}`);


      console.log('Fetching pending incentives:', url);

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch pending incentives: ${response.status}`);
      }

      const data = await response.json();
      console.log('Pending incentives data:', data);
      setPendingIncentives(data);
    } catch (err) {
      console.error('Error fetching pending incentives:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalModal = (incentive: PendingIncentive, action: 'approve' | 'reject') => {
    setSelectedIncentive(incentive);
    setApprovalAction(action);
    setApprovalNotes('');
    setIsApprovalModalOpen(true);
  };

  const handleApprovalAction = async () => {
    if (!selectedIncentive) return;

    try {
      setProcessingApproval(true);

      const action = approvalAction === 'approve' ? 'approve' : 'reject';
      const url = getWeaveinApiUrl(`/api/member-incentive-payouts/${selectedIncentive.id}/${action}`);

      console.log(`${action === 'approve' ? 'Approving' : 'Rejecting'} payout:`, selectedIncentive.id);

      const response = await authFetch(url, {
        method: 'PUT',
        jsonBody: {
          notes: approvalNotes
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${action} payout`);
      }

      console.log(`Payout ${action}ed successfully`);

      // Reset modal state
      setIsApprovalModalOpen(false);
      setSelectedIncentive(null);
      setApprovalNotes('');

      // Refresh the list
      fetchPendingIncentives();
    } catch (err) {
      console.error('Error processing approval:', err);
      alert(err instanceof Error ? err.message : 'Failed to process approval');
    } finally {
      setProcessingApproval(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const totalPendingAmount = pendingIncentives.reduce((sum, p) => sum + p.payout_amount, 0);
  const cboTotalAmount = pendingIncentives.reduce((sum, p) => sum + p.cbo_payout_amount, 0);
  const memberTotalAmount = pendingIncentives.reduce((sum, p) => sum + p.member_payout_amount, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-2xl font-bold text-gray-900">{t('incentives.pending_milestone_approvals', 'Pending Milestone Approvals')}</h2>
          )}
          <p className="text-sm text-gray-600 mt-1">
            {t('incentives.review_and_approve_milestonebased_incentive', 'Review and approve milestone-based incentive payouts')}
          </p>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          {t('incentives.export', 'Export')}
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">{t('incentives.pending_approvals', 'Pending Approvals')}</p>
              <p className="text-3xl font-bold text-yellow-900 mt-2">
                {pendingIncentives.length}
              </p>
            </div>
            <div className="bg-yellow-500 rounded-full p-3">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">{t('incentives.total_amount', 'Total Amount')}</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                {formatCurrency(totalPendingAmount)}
              </p>
            </div>
            <div className="bg-green-500 rounded-full p-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">{t('incentives.cbo_payouts', 'CBO Payouts')}</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {formatCurrency(cboTotalAmount)}
              </p>
            </div>
            <div className="bg-blue-500 rounded-full p-3">
              <Target className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">{t('incentives.member_payouts', 'Member Payouts')}</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                {formatCurrency(memberTotalAmount)}
              </p>
            </div>
            <div className="bg-purple-500 rounded-full p-3">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{t('incentives.filters', 'Filters:')}</span>
          </div>

          <Select value={filterPayoutMethod} onValueChange={setFilterPayoutMethod}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder={t('incentives.all_payment_methods', 'All Payment Methods')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">{t('incentives.all_methods', 'All Methods')}</SelectItem>
              {PAYOUT_METHODS.map((method) => (
                <SelectItem key={method.value} value={method.value}>
                  {method.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setFilterPayoutMethod('');
              fetchPendingIncentives();
            }}
          >
            {t('incentives.clear_filters', 'Clear Filters')}
          </Button>
        </div>
      </Card>

      {/* Pending Incentives List */}
      {pendingIncentives.length === 0 ? (
        <Card className="p-12 text-center">
          <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('incentives.no_pending_approvals', 'No Pending Approvals')}
          </h3>
          <p className="text-sm text-gray-600">
            {t('incentives.all_milestone_incentives_have_been', 'All milestone incentives have been processed')}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {pendingIncentives.map((incentive) => {
            const PaymentMethodIcon =
              PAYOUT_METHODS.find((m) => m.value === incentive.payout_method)?.icon || DollarSign;

            return (
              <Card key={incentive.id} className="p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="bg-yellow-100 rounded-full p-2">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {formatCurrency(incentive.payout_amount)}
                          </h4>
                          <Badge className="bg-yellow-100 text-yellow-800">
                            {t('incentives.pending_approval', 'Pending Approval')}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Users className="w-3 h-3" />
                            <span>{incentive.member_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Target className="w-3 h-3" />
                            <span>{incentive.milestone_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <PaymentMethodIcon className="w-3 h-3" />
                            <span>
                              {PAYOUT_METHODS.find((m) => m.value === incentive.payout_method)?.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs">{incentive.payout_number}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-v2accent hover:bg-v2accent/90"
                          onClick={() => openApprovalModal(incentive, 'approve')}
                        >
                          <ThumbsUp className="w-4 h-4 mr-1" />
                          {t('incentives.approve', 'Approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => openApprovalModal(incentive, 'reject')}
                        >
                          <ThumbsDown className="w-4 h-4 mr-1" />
                          {t('incentives.reject', 'Reject')}
                        </Button>
                      </div>
                    </div>

                    {/* Payout Breakdown */}
                    <div className="grid grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('incentives.cbo_payout', 'CBO Payout')}</p>
                        <p className="text-sm font-semibold text-blue-600">
                          {formatCurrency(incentive.cbo_payout_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('incentives.member_payout', 'Member Payout')}</p>
                        <p className="text-sm font-semibold text-purple-600">
                          {formatCurrency(incentive.member_payout_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">{t('incentives.scheduled_date', 'Scheduled Date')}</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(incentive.scheduled_date)}
                        </p>
                      </div>
                    </div>

                    {/* Milestone Info */}
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <Badge variant="outline" className="text-xs">
                        {incentive.milestone_type}
                      </Badge>
                      <span>•</span>
                      <span>{incentive.program_name}</span>
                      {incentive.completion_date && (
                        <>
                          <span>•</span>
                          <span>Completed: {formatDate(incentive.completion_date)}</span>
                        </>
                      )}
                    </div>

                    {/* Card Integration Notice */}
                    {incentive.payout_method === 'CARD' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-blue-800">
                          {t('incentives.approval_will_trigger_prepaid_card', 'Approval will trigger prepaid card issuance for member payout')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Approval/Rejection Modal */}
      <Dialog open={isApprovalModalOpen} onOpenChange={setIsApprovalModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {approvalAction === 'approve' ? 'Approve' : 'Reject'} Incentive Payout
            </DialogTitle>
          </DialogHeader>

          {selectedIncentive && (
            <div className="space-y-4 mt-4">
              {/* Payout Summary */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('incentives.member', 'Member:')}</span>
                  <span className="font-semibold">{selectedIncentive.member_name}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('incentives.milestone', 'Milestone:')}</span>
                  <span className="font-medium">{selectedIncentive.milestone_name}</span>
                </div>

                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-sm text-gray-600">{t('incentives.cbo_payout_1', 'CBO Payout:')}</span>
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(selectedIncentive.cbo_payout_amount)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('incentives.member_payout_1', 'Member Payout:')}</span>
                  <span className="text-lg font-bold text-purple-600">
                    {formatCurrency(selectedIncentive.member_payout_amount)}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-sm font-semibold text-gray-700">{t('incentives.total_amount_1', 'Total Amount:')}</span>
                  <span className="text-xl font-bold text-green-600">
                    {formatCurrency(selectedIncentive.payout_amount)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('incentives.payment_method', 'Payment Method:')}</span>
                  <span className="font-medium">
                    {PAYOUT_METHODS.find((m) => m.value === selectedIncentive.payout_method)?.label}
                  </span>
                </div>
              </div>

              {/* Card Integration Warning */}
              {approvalAction === 'approve' && selectedIncentive.payout_method === 'CARD' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-1">{t('incentives.prepaid_card_issuance', 'Prepaid Card Issuance')}</p>
                      <p>
                        Approving this payout will automatically trigger a prepaid card issuance
                        for {formatCurrency(selectedIncentive.member_payout_amount)} to the member.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes Field */}
              <div>
                <Label htmlFor="approval_notes">
                  {approvalAction === 'approve' ? 'Approval' : 'Rejection'} Notes
                </Label>
                <Textarea
                  id="approval_notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder={`Enter notes for ${approvalAction}...`}
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsApprovalModalOpen(false)}
                  disabled={processingApproval}
                >
                  {t('incentives.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={handleApprovalAction}
                  disabled={processingApproval}
                  className={
                    approvalAction === 'approve'
                      ? 'bg-v2accent hover:bg-v2accent/90'
                      : 'bg-red-600 hover:bg-red-700'
                  }
                >
                  {processingApproval ? (
                    <>
                      <LoadingSpinner />
                      <span className="ml-2">{t('incentives.processing', 'Processing...')}</span>
                    </>
                  ) : approvalAction === 'approve' ? (
                    <>
                      <ThumbsUp className="w-4 h-4 mr-2" />
                      {t('incentives.approve_payout', 'Approve Payout')}
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="w-4 h-4 mr-2" />
                      {t('incentives.reject_payout', 'Reject Payout')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingMilestoneApprovals;
